// Helpers shared by content files (24.2: shared helpers live in core, content never imports
// content). Pure functions over ReadContext; no game rules beyond what their names say.
import { mod } from './modifiers';
import { buildingsOf, conditionFactor, population, settlementTrust } from './queries';
import type { EventChoiceDef } from './registry';
import { createRng, hashString, type Rng } from './rng';
// Section 19 and 14 event helpers (stream E2).
import type { ArrivalDecision, BuildingId, EventPrompt, PersonId, PromptId, Season } from './types';
import type {
  ActiveEffect,
  Building,
  BuildingTypeId,
  Delta,
  EventId,
  JobId,
  Modifier,
  Person,
  PreviewLine,
  PriorityGroupId,
  ReadContext,
  ReportEntry,
  Resource,
  Target,
  ValidationProblem,
} from './types';

type ModHook = (ctx: ReadContext, target: Target) => readonly Modifier[];

/** Concatenate several modifier hooks into one (in order; no copy when at most one applies). */
export function combineModifiers(...hooks: ModHook[]): ModHook {
  return (ctx, target) => {
    let out: readonly Modifier[] = NONE;
    let owned = false;
    for (const h of hooks) {
      const mods = h(ctx, target);
      if (mods.length === 0) continue;
      if (out.length === 0) {
        out = mods;
        continue;
      }
      if (!owned) {
        out = [...out];
        owned = true;
      }
      (out as Modifier[]).push(...mods);
    }
    return out;
  };
}

const NONE: readonly Modifier[] = Object.freeze([]);

/** The person a person-scoped target refers to, if they exist. */
export function targetPerson(ctx: ReadContext, target: Target): Person | null {
  if (!('personId' in target)) return null;
  return ctx.state.people[target.personId] ?? null;
}

/** Sum of condition factors over buildings of a type (0 when none are standing). */
export function effective(ctx: ReadContext, type: BuildingTypeId): number {
  let total = 0;
  for (const b of buildingsOf(ctx.state, type)) total += conditionFactor(ctx.cfg, b);
  return total;
}

/** Section 7 and 9: a building's added stock capacity, scaled by condition. */
export function capacityHook(
  type: BuildingTypeId,
  capacity: Partial<Record<Resource, number>>,
): ModHook {
  return (ctx, target) => {
    if (target.kind !== 'capacity') return [];
    const add = capacity[target.resource];
    if (add === undefined) return [];
    const n = effective(ctx, type);
    return n > 0
      ? [mod.add(`building:${type}`, add * n, `${buildingsOf(ctx.state, type).length} built`)]
      : [];
  };
}

/**
 * Section 9 and D-13 (stream M3a): a building instance's effect strength 0..1 for the
 * `buildingEffect` target (explain() on the Build tab): full at condition 50+, linear below, and
 * 0 while `off` returns a reason (a frozen well, a palisade without its guards).
 */
export function buildingEffectHook(
  type: BuildingTypeId,
  off?: (ctx: ReadContext, b: Building) => string | null,
): ModHook {
  return (ctx, target) => {
    if (target.kind !== 'buildingEffect') return [];
    const b = ctx.state.buildings[target.buildingId];
    if (b?.type !== type) return [];
    const reason = off === undefined ? null : off(ctx, b);
    if (reason !== null) return [mod.set(`building:${type}`, 0, reason)];
    return [
      mod.mul(
        `building:${type}`,
        conditionFactor(ctx.cfg, b),
        `condition ${Math.round(b.condition)}`,
      ),
    ];
  };
}

/** Section 6: heavy jobs add food demand for their workers. */
export function heavyJobDemand(jobId: JobId, surcharge: number): ModHook {
  return (ctx, target) => {
    if (target.kind !== 'demand' || target.resource !== 'food') return [];
    const p = ctx.state.people[target.personId];
    return p?.job === jobId ? [mod.add(`job:${jobId}`, surcharge, 'heavy work')] : [];
  };
}

/** Output modifiers for workers of one job. */
export function jobOutput(
  jobId: JobId,
  fn: (ctx: ReadContext, p: Person) => readonly Modifier[],
): ModHook {
  return (ctx, target) => {
    if (target.kind !== 'output' || target.jobId !== jobId) return [];
    const p = ctx.state.people[target.personId];
    return p === undefined ? [] : fn(ctx, p);
  };
}

/** A stock delta helper so content reads like the spec tables. */
export function produced(resource: Resource, amount: number): Delta {
  return { kind: 'stock', resource, amount, flow: 'production' };
}

export const noDeltas = (): readonly Delta[] => [];
export const alwaysEligible = (): boolean => true;
export const neverRestricts = (): boolean => false;
export const noPreview = (): readonly PreviewLine[] => [];
export const noProblem = (): ValidationProblem | null => null;
export const noChangeCost = (): number => 0;

/** Placeholder event hooks: record a typed entry and nothing else (events stream fills in). */
export function placeholderEventText(eventId: EventId): (ctx: ReadContext) => ReportEntry {
  return () => ({ kind: 'event', eventId, params: {} });
}

export function placeholderEventApply(eventId: EventId): (ctx: ReadContext) => readonly Delta[] {
  return () => [{ kind: 'report', entry: { kind: 'event', eventId, params: {} } }];
}

/** Section 17 groups used by norms to recognise a precedent in the food priority order. */
export const WORKER_GROUPS = [
  'farmers',
  'food-producers',
  'water-carriers',
  'builders',
  'toolmakers',
  'healers',
  'teachers',
  'guards',
  'other-workers',
] as const satisfies readonly PriorityGroupId[];
export const CHILD_GROUPS = [
  'children-under-6',
  'children-6-14',
] as const satisfies readonly PriorityGroupId[];
export const DEPENDANT_GROUPS = [
  'pregnant',
  'children-under-6',
  'children-6-14',
  'elders',
] as const satisfies readonly PriorityGroupId[];

/**
 * Section 16 (Stream D): a food-rationing precedent read from a report entry: the rationing
 * phase's `rationing-outcome`, or the legacy `shortage` entry (which does not say who went short,
 * so `short` is null and the priority order in force is read from state).
 */
export interface RationPrecedent {
  policy: string;
  order: readonly PriorityGroupId[];
  /** Groups with a member rationed short; null when the entry does not say. */
  short: readonly PriorityGroupId[] | null;
}

export function foodPrecedent(ctx: ReadContext, entry: ReportEntry): RationPrecedent | null {
  if (entry.kind === 'rationing-outcome' && entry.resource === 'food')
    return { policy: entry.policy, order: entry.groupsOrder, short: entry.shortGroups };
  if (entry.kind === 'shortage' && entry.resource === 'food')
    return { policy: entry.policy, order: ctx.state.policies.foodPriority, short: null };
  return null;
}

/** Did any of `groups` go short? `unknown` is returned for legacy entries that do not say. */
export function wentShort(
  p: RationPrecedent,
  groups: readonly PriorityGroupId[],
  unknown: boolean,
): boolean {
  return p.short === null ? unknown : p.short.some((g) => groups.includes(g));
}

/** True if the first of `first` appears in `order` before the first of `second` (absent = last). */
export function ranksBefore(
  order: readonly PriorityGroupId[],
  first: readonly PriorityGroupId[],
  second: readonly PriorityGroupId[],
): boolean {
  const rank = (groups: readonly PriorityGroupId[]): number => {
    const found = order.findIndex((g) => groups.includes(g));
    return found < 0 ? Number.MAX_SAFE_INTEGER : found;
  };
  const a = rank(first);
  return a !== Number.MAX_SAFE_INTEGER && a < rank(second);
}

/**
 * Placeholder choice with no effect yet (the events stream replaces `apply`). The event-choices
 * phase records every answered choice as an `event-choice` entry itself.
 */
export function placeholderChoice(_eventId: EventId, id: string, label: string): EventChoiceDef {
  return { id, label, preview: null, apply: noDeltas };
}

/**
 * Generic modifiers for an event effect in force, driven by its params (Section 19 effects):
 * harvestMul and cropsMul scale the harvest, warmthMul Winter warmth demand, illnessMul the
 * illness roll, waterMul water carriers, forageMul foragers.
 */
export function effectParamModifiers(
  _ctx: ReadContext,
  target: Target,
  effect: ActiveEffect,
): readonly Modifier[] {
  const source = `event:${effect.eventId}`;
  const p = effect.params;
  const out: Modifier[] = [];
  const push = (value: number | undefined, note: string): void => {
    if (value !== undefined) out.push(mod.mul(source, value, note));
  };
  switch (target.kind) {
    case 'harvestYield':
      push(p.harvestMul, 'harvest');
      push(p.cropsMul, 'crops');
      break;
    case 'demand':
      if (target.resource === 'warmth') push(p.warmthMul, 'warmth');
      break;
    case 'illnessRisk':
      push(p.illnessMul, 'illness');
      break;
    case 'output':
      if (target.jobId === 'water-carrier') push(p.waterMul, 'water');
      if (target.jobId === 'forager') push(p.forageMul, 'forage');
      break;
    default:
      break;
  }
  return out;
}

// ---- Section 19 and 14 event helpers (stream E2, ADR-0016). An event's randomness comes from
// the seed drawn with the deck (`ctx.event.seed`) or carried by its prompt (`params.seed`), never
// from the main stream, so the deck stays decision-independent (24.13).

/** The turn whose events phase drew the running event (its prompt and effects key on it). */
export function eventTurn(ctx: ReadContext): number {
  return ctx.event?.turn ?? ctx.season.turn;
}

/** The running event's seed; a stable fallback when a hook runs outside the events phase. */
export function eventSeed(ctx: ReadContext, eventId: EventId): number {
  return ctx.event?.seed ?? hashString(`${ctx.state.seed}:${eventId}:${ctx.season.turn}`);
}

/** A fresh stream for one use of the event's randomness: the same salt gives the same numbers. */
export function eventRng(ctx: ReadContext, eventId: EventId, salt: string): Rng {
  return createRng((eventSeed(ctx, eventId) ^ hashString(salt)) >>> 0).rng;
}

/** Randomness for a choice, from the seed its prompt carries. */
export function choiceRng(prompt: EventPrompt, salt: string): Rng {
  const seed = typeof prompt.params.seed === 'number' ? prompt.params.seed : hashString(prompt.id);
  return createRng((seed ^ hashString(salt)) >>> 0).rng;
}

/** A number the event stored in its prompt (0 when absent, e.g. in the registry contract test). */
export function promptNumber(prompt: EventPrompt, key: string): number {
  const v = prompt.params[key];
  return typeof v === 'number' ? v : 0;
}

/** A string the event stored in its prompt ('' when absent). */
export function promptString(prompt: EventPrompt, key: string): string {
  const v = prompt.params[key];
  return typeof v === 'string' ? v : '';
}

export function eventPromptId(eventId: EventId, turn: number): PromptId {
  return `event:${eventId}:t${turn}`;
}

export interface EventPromptOptions {
  params?: Record<string, number | string>;
  subjectIds?: PersonId[];
  defaultChoiceId: string;
  /** The choices offered now (all when absent); e.g. mediation needs trust 60+. */
  offered?: readonly string[];
}

/** Open the event's prompt (Section 19: presented in the next Report, answered in phase 2). */
export function eventPrompt(
  ctx: ReadContext,
  eventId: EventId,
  choices: readonly EventChoiceDef[],
  opts: EventPromptOptions,
): Delta {
  const turn = eventTurn(ctx);
  const offered = choices.filter((c) => opts.offered === undefined || opts.offered.includes(c.id));
  const prompt: EventPrompt = {
    kind: 'event',
    id: eventPromptId(eventId, turn),
    turn,
    eventId,
    params: { ...opts.params, seed: eventSeed(ctx, eventId) },
    subjectIds: opts.subjectIds ?? [],
    groupId: null,
    choices: offered.map((c) => ({ id: c.id, label: c.label })),
    defaultChoiceId: offered.some((c) => c.id === opts.defaultChoiceId)
      ? opts.defaultChoiceId
      : (offered[0]?.id ?? opts.defaultChoiceId),
  };
  return { kind: 'prompt', prompt };
}

/** The event's headline entry (prose: `event.<id>` or `event.<id>.<variant>` in ui/text/en.ts). */
export function eventEntry(
  eventId: EventId,
  params: Record<string, number | string> = {},
): ReportEntry {
  return { kind: 'event', eventId, params };
}

/** What a choice did; the event-choices phase adds a bare entry when a choice records none. */
export function choiceOutcome(
  prompt: EventPrompt,
  choiceId: string,
  params: Record<string, number | string> = {},
): Delta {
  return {
    kind: 'report',
    entry: { kind: 'event-choice', eventId: prompt.eventId, promptId: prompt.id, choiceId, params },
  };
}

/** An effect in force for `seasons` seasons from turn `from` (Section 19 effects that last). */
export function effectDelta(
  eventId: EventId,
  tag: string,
  from: number,
  seasons: number,
  params: Record<string, number>,
  subjectIds?: PersonId[],
): Delta {
  const effect: ActiveEffect = {
    id: `${eventId}:${tag}:t${from}`,
    eventId,
    sinceTurn: from,
    untilTurn: from + Math.max(1, seasons) - 1,
    params,
  };
  if (subjectIds !== undefined) effect.subjectIds = subjectIds;
  return { kind: 'effect', effect };
}

/** Is the effect in force in the season being resolved? */
export function effectActive(ctx: ReadContext, effect: ActiveEffect): boolean {
  return effect.sinceTurn <= ctx.season.turn && ctx.season.turn <= effect.untilTurn;
}

/** The season of a turn relative to the one being resolved. */
export function seasonOfTurn(ctx: ReadContext, turn: number): Season {
  const order = ctx.cfg.seasons.order;
  const n = order.length;
  const i = (((ctx.season.seasonIndex + turn - ctx.season.turn) % n) + n) % n;
  return order[i] ?? ctx.season.season;
}

/** Section 14: the first Spring or Summer turn at or after `from` (departures leave then). */
export function departureTurn(ctx: ReadContext, from: number): number {
  const seasons = ctx.cfg.migration.departureSeasons;
  for (let t = from; t < from + ctx.cfg.seasons.order.length; t++)
    if (seasons.includes(seasonOfTurn(ctx, t))) return t;
  return from;
}

/** Adults (not children, elders count) in sorted id order, optionally filtered. */
export function adultsOf(ctx: ReadContext, pred: (p: Person) => boolean = () => true): Person[] {
  const out: Person[] = [];
  for (const id of Object.keys(ctx.state.people).sort() as PersonId[]) {
    const p = ctx.state.people[id];
    if (p !== undefined && p.ageSeasons >= ctx.cfg.people.adultAtSeasons && pred(p)) out.push(p);
  }
  return out;
}

/** Beds standing (condition above 0), the Section 9 shelter supply. */
export function bedsStanding(ctx: ReadContext): number {
  let beds = 0;
  for (const id of Object.keys(ctx.state.buildings).sort() as BuildingId[]) {
    const b = ctx.state.buildings[id];
    if (b !== undefined && b.condition > 0) beds += ctx.registry.buildings[b.type].beds;
  }
  return beds;
}

/**
 * Section 14 and 16: arrivals come more often above trust 75 (word spreads), half as often under
 * the founders-first norm, and not at all at the population cap (Section 26: 200).
 */
export function arrivalWeight(ctx: ReadContext, base: number): number {
  const cfg = ctx.cfg;
  if (population(ctx.state) >= cfg.migration.populationCap) return 0;
  let w = base;
  if (settlementTrust(ctx.state) > cfg.trust.bonusAbove) w *= cfg.migration.arrivalsTrustMul;
  if (ctx.state.norms['founders-first'].established) w *= cfg.norms['founders-first'].arrivalsMul;
  return w;
}

/** Section 14: room left under the population cap. */
export function arrivalRoom(ctx: ReadContext): number {
  return Math.max(0, ctx.cfg.migration.populationCap - population(ctx.state));
}

function arrivalPreview(decision: ArrivalDecision) {
  return (ctx: ReadContext, prompt: EventPrompt): readonly PreviewLine[] => {
    const group = prompt.groupId === null ? undefined : ctx.state.arrivalGroups[prompt.groupId];
    const people = group?.candidates.length ?? prompt.subjectIds.length;
    const spare = Math.max(0, bedsStanding(ctx) - population(ctx.state));
    switch (decision) {
      case 'admit-all':
        return [{ key: 'arrival-admit', params: { people, beds: spare } }];
      case 'admit-some':
        return [{ key: 'arrival-admit-some', params: { beds: spare } }];
      case 'guests':
        return [
          { key: 'arrival-guests', params: { people, seasons: ctx.cfg.migration.guestSeasons } },
        ];
      case 'turn-away': {
        let devout = 0;
        for (const p of Object.values(ctx.state.people)) if (p.traits.includes('devout')) devout++;
        const starving =
          group !== undefined &&
          group.reason === 'famine' &&
          group.candidates.some((c) => c.ageSeasons < ctx.cfg.people.adultAtSeasons);
        return [
          {
            key: starving ? 'arrival-turn-away-starving' : 'arrival-turn-away',
            params: { devout },
          },
        ];
      }
    }
  };
}

/**
 * Section 14: the arrival screen's choices (admit all, admit some by name, admit as guests, turn
 * away), each a `gate` decision. `ids` maps a decision to this event's choice id and label.
 */
export function arrivalChoices(
  eventId: EventId,
  ids: Partial<Record<ArrivalDecision, { id: string; label: string }>>,
): EventChoiceDef[] {
  const out: EventChoiceDef[] = [];
  for (const decision of ['admit-all', 'admit-some', 'guests', 'turn-away'] as const) {
    const choice = ids[decision];
    if (choice === undefined) continue;
    out.push({
      id: choice.id,
      label: choice.label,
      preview: arrivalPreview(decision),
      apply: (_ctx, prompt, answer) =>
        prompt.groupId === null
          ? []
          : [
              {
                kind: 'gate',
                groupId: prompt.groupId,
                decision,
                personIds: decision === 'admit-some' ? answer.personIds : [],
                source: `event:${eventId}`,
              },
            ],
    });
  }
  return out;
}
